/**
 * PII (Personally Identifiable Information) Protection Utilities
 * Provides masking and sanitization for sensitive data
 */

const shouldMaskPII = import.meta.env.VITE_MASK_PII_DATA === 'true';
const enableLogging = import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true';

export interface PIIField {
  value: string | null | undefined;
  type: 'phone' | 'email' | 'address' | 'name' | 'custom';
}

/**
 * Masks phone numbers
 * Example: 9876543210 -> ******3210
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  if (!shouldMaskPII) return phone;
  
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 4) return '****';
  
  return '*'.repeat(cleaned.length - 4) + cleaned.slice(-4);
}

/**
 * Masks email addresses
 * Example: user@example.com -> u***@example.com
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  if (!shouldMaskPII) return email;
  
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  
  const maskedLocal = localPart.length > 2 
    ? localPart[0] + '*'.repeat(localPart.length - 1)
    : '***';
  
  return `${maskedLocal}@${domain}`;
}

/**
 * Masks address information
 * Example: 123 Main St, Apt 4B -> ***in St, Apt **
 */
export function maskAddress(address: string | null | undefined): string {
  if (!address) return '';
  if (!shouldMaskPII) return address;
  
  // Mask first half of address
  const length = address.length;
  const visibleLength = Math.floor(length * 0.3);
  const maskedLength = length - visibleLength;
  
  return '*'.repeat(maskedLength) + address.slice(-visibleLength);
}

/**
 * Masks person name
 * Example: John Doe -> J*** D**
 */
export function maskName(name: string | null | undefined): string {
  if (!name) return '';
  if (!shouldMaskPII) return name;
  
  return name
    .split(' ')
    .map(part => part.length > 0 ? part[0] + '*'.repeat(Math.min(part.length - 1, 3)) : part)
    .join(' ');
}

/**
 * Generic masking function
 */
export function maskPII(field: PIIField): string {
  if (!field.value) return '';
  
  switch (field.type) {
    case 'phone':
      return maskPhone(field.value);
    case 'email':
      return maskEmail(field.value);
    case 'address':
      return maskAddress(field.value);
    case 'name':
      return maskName(field.value);
    case 'custom':
      return shouldMaskPII 
        ? '*'.repeat(Math.min(field.value.length, 8))
        : field.value;
    default:
      return field.value;
  }
}

/**
 * Sanitizes object by masking all PII fields
 */
export function sanitizeObject<T extends Record<string, any>>(
  obj: T,
  piiFields: Array<keyof T & string>
): T {
  if (!shouldMaskPII) return obj;
  
  const sanitized = { ...obj };
  
  piiFields.forEach(field => {
    if (sanitized[field]) {
      // Determine field type based on field name
      let type: PIIField['type'] = 'custom';
      if (field.includes('phone') || field.includes('mobile')) type = 'phone';
      else if (field.includes('email')) type = 'email';
      else if (field.includes('address')) type = 'address';
      else if (field.includes('name')) type = 'name';
      
      sanitized[field] = maskPII({ value: String(sanitized[field]), type }) as any;
    }
  });
  
  return sanitized;
}

/**
 * Safe console logging that masks PII data
 */
export const safeConsole = {
  log: (...args: any[]) => {
    if (enableLogging) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (enableLogging) {
      console.error(...args);
    }
  },
  warn: (...args: any[]) => {
    if (enableLogging) {
      console.warn(...args);
    }
  },
  info: (...args: any[]) => {
    if (enableLogging) {
      console.info(...args);
    }
  },
};

/**
 * Validates if data contains potential PII
 */
export function containsPII(data: string): boolean {
  // Check for patterns that might indicate PII
  const patterns = [
    /\b\d{10}\b/, // Phone numbers
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/, // Email
    /\b\d{3}-\d{2}-\d{4}\b/, // SSN-like patterns
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/, // Credit card-like patterns
  ];
  
  return patterns.some(pattern => pattern.test(data));
}

/**
 * Redacts potential PII from strings
 */
export function redactPII(text: string): string {
  if (!shouldMaskPII) return text;
  
  return text
    // Redact phone numbers
    .replace(/\b\d{10}\b/g, '**********')
    // Redact email addresses
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '***@***.***')
    // Redact SSN-like patterns
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****')
    // Redact credit card-like patterns
    .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '****-****-****-****');
}

const CREDENTIAL_KEYS = new Set([
  'password', 'oldpassword', 'newpassword', 'confirmpassword',
  'token', 'secret', 'otp', 'pin', 'accesstoken', 'refreshtoken', 'apikey',
]);

/**
 * Strips credential fields from an object before logging.
 * Always redacts regardless of VITE_MASK_PII_DATA — passwords must never appear in logs.
 */
export function maskCredentials(body: unknown): unknown {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body as Record<string, unknown>)) {
    out[k] = CREDENTIAL_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : v;
  }
  return out;
}

export const piiUtils = {
  maskPhone,
  maskEmail,
  maskAddress,
  maskName,
  maskPII,
  sanitizeObject,
  containsPII,
  redactPII,
  maskCredentials,
  safeConsole,
};
